import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";

/* ─────────────────────────────────────────────────────────────
   KUNDA production boot — no more silent white screens.

   1. <ErrorBoundary> catches any React render/runtime error and
      shows a friendly recovery screen instead of a blank page.
   2. A global boot guard catches errors that happen BEFORE React
      mounts (module-level crashes, chunk loading failures) and
      writes a visible message with the actual error text, so we
      can always see WHY something failed.
───────────────────────────────────────────────────────────── */

var FALLBACK_STYLE =
  "min-height:100vh;display:flex;flex-direction:column;align-items:center;" +
  "justify-content:center;text-align:center;padding:32px;background:#F8F6F3;" +
  "font-family:Inter,system-ui,sans-serif;color:#1A1410;";

function renderFatal(message) {
  var root = document.getElementById("root");
  if (!root) return;
  root.innerHTML =
    '<div style="' + FALLBACK_STYLE + '">' +
    '<div style="font-size:44px;margin-bottom:14px;">🌿</div>' +
    '<h1 style="font-size:22px;margin:0 0 8px;">Something went wrong</h1>' +
    '<p style="font-size:15px;color:#7A6E60;max-width:340px;margin:0 0 20px;">' +
    "KUNDA hit an unexpected error. Tap below to reload — if it keeps happening, " +
    "please contact KUNDA support on WhatsApp.</p>" +
    '<button onclick="window.location.reload()" style="background:#1A1410;color:#fff;' +
    'border:none;border-radius:16px;padding:15px 32px;font-size:15px;font-weight:700;' +
    'cursor:pointer;">Reload KUNDA</button>' +
    '<p style="font-size:11px;color:#A89B8B;max-width:340px;margin:22px 0 0;word-break:break-word;">' +
    String(message || "").slice(0, 300) +
    "</p></div>";
}

/* Boot guard — catches crashes before/outside React (incl. failed JS chunks) */
window.addEventListener("error", function (e) {
  var root = document.getElementById("root");
  if (root && root.childElementCount === 0) {
    renderFatal(e && e.message);
  }
});
window.addEventListener("unhandledrejection", function (e) {
  var root = document.getElementById("root");
  if (root && root.childElementCount === 0) {
    renderFatal(e && e.reason && (e.reason.message || String(e.reason)));
  }
});

/* React error boundary — catches render/runtime errors after mount */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error: error };
  }
  componentDidCatch(error, info) {
    /* Log for debugging in the browser console */
    console.error("KUNDA ErrorBoundary:", error, info);
  }
  render() {
    if (this.state.error) {
      var msg = this.state.error && this.state.error.message ? this.state.error.message : "";
      return React.createElement(
        "div",
        { style: {
            minHeight: "100vh", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", textAlign: "center",
            padding: "32px", background: "#F8F6F3",
            fontFamily: "Inter, system-ui, sans-serif", color: "#1A1410"
        } },
        React.createElement("div", { style: { fontSize: "44px", marginBottom: "14px" } }, "🌿"),
        React.createElement("h1", { style: { fontSize: "22px", margin: "0 0 8px" } }, "Something went wrong"),
        React.createElement(
          "p",
          { style: { fontSize: "15px", color: "#7A6E60", maxWidth: "340px", margin: "0 0 20px" } },
          "KUNDA hit an unexpected error. Tap below to reload — if it keeps happening, please contact KUNDA support on WhatsApp."
        ),
        React.createElement(
          "button",
          {
            onClick: function () { window.location.reload(); },
            style: {
              background: "#1A1410", color: "#fff", border: "none",
              borderRadius: "16px", padding: "15px 32px", fontSize: "15px",
              fontWeight: 700, cursor: "pointer"
            }
          },
          "Reload KUNDA"
        ),
        React.createElement(
          "p",
          { style: { fontSize: "11px", color: "#A89B8B", maxWidth: "340px", margin: "22px 0 0", wordBreak: "break-word" } },
          String(msg).slice(0, 300)
        )
      );
    }
    return this.props.children;
  }
}

var rootEl = document.getElementById("root");
var root = ReactDOM.createRoot(rootEl);
root.render(
  React.createElement(
    React.StrictMode,
    null,
    React.createElement(ErrorBoundary, null, React.createElement(App, null))
  )
);